import React from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, MessageCircle } from 'lucide-react';
import { useConfirmPasswordReset } from '../../features/auth/hooks/useAuth';
import { ROUTES } from '../../config/routes';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/alert';
import { usePageTitle } from '../../hooks/usePageTitle';

const schema = z
  .object({
    code: z.string().min(1, '確認コードを入力してください'),
    new_password: z
      .string()
      .min(8, 'パスワードは8文字以上で入力してください')
      .regex(/[a-z]/, '小文字を含めてください')
      .regex(/[A-Z]/, '大文字を含めてください')
      .regex(/[0-9]/, '数字を含めてください'),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'パスワードが一致しません',
    path: ['confirm_password'],
  });

type FormData = z.infer<typeof schema>;

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  borderRadius: '20px',
  boxShadow: '0 4px 24px rgba(15,23,42,0.08)',
  padding: '40px',
  width: '100%',
};

export const ResetPasswordPage: React.FC = () => {
  usePageTitle('パスワードの再設定');
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const { mutate, isPending } = useConfirmPasswordReset();
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  if (!email) {
    return (
      <div style={CARD_STYLE}>
        <Alert variant="error" className="mb-4">
          無効なリンクです。パスワードリセットのメールを再送してください。
        </Alert>
        <div className="text-center">
          <RouterLink to={ROUTES.LOGIN} className="text-sm text-primary hover:underline">
            ログイン画面へ戻る
          </RouterLink>
        </div>
      </div>
    );
  }

  const onSubmit = (data: FormData) => {
    mutate({ email, code: data.code, new_password: data.new_password });
  };

  return (
    <div style={CARD_STYLE}>
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '8px',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#0a7a72',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MessageCircle size={20} color="white" />
          </div>
          <span style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A' }}>EntryBOT</span>
        </div>
        <p style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
          新しいパスワードを設定
        </p>
        <p style={{ fontSize: '13px', color: '#64748B' }}>
          メールに記載の確認コードと新しいパスワードを入力してください
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="mb-4">
          <label htmlFor="reset-code" className="block text-sm font-medium mb-1.5">確認コード</label>
          <Input
            {...register('code')}
            id="reset-code"
            type="text"
            placeholder="492817"
            inputMode="numeric"
            className="!text-[16px] md:!text-sm"
            aria-invalid={!!errors.code}
            aria-describedby="reset-code-error"
          />
          {errors.code && (
            <p id="reset-code-error" className="text-sm text-red-500 mt-1">
              {errors.code.message}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="reset-new-password" className="block text-sm font-medium mb-1.5">新しいパスワード</label>
          <div className="relative">
            <Input
              {...register('new_password')}
              id="reset-new-password"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="パスワードを入力"
              className="pr-10 !text-[16px] md:!text-sm"
              aria-invalid={!!errors.new_password}
              aria-describedby="reset-new-password-error"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowNewPassword(!showNewPassword)}
              tabIndex={-1}
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.new_password && (
            <p id="reset-new-password-error" className="text-sm text-red-500 mt-1">
              {errors.new_password.message}
            </p>
          )}
        </div>

        <div className="mb-6">
          <label htmlFor="reset-confirm-password" className="block text-sm font-medium mb-1.5">新しいパスワード（確認）</label>
          <div className="relative">
            <Input
              {...register('confirm_password')}
              id="reset-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="パスワードを入力"
              className="pr-10 !text-[16px] md:!text-sm"
              aria-invalid={!!errors.confirm_password}
              aria-describedby="reset-confirm-password-error"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirm_password && (
            <p id="reset-confirm-password-error" className="text-sm text-red-500 mt-1">
              {errors.confirm_password.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full !h-auto !py-3.5 !text-base !font-bold !rounded-lg md:!py-3 md:!text-[15px]"
          disabled={isPending}
        >
          {isPending ? '処理中...' : 'パスワードを再設定'}
        </Button>
      </form>
    </div>
  );
};
